/**
 * groupByUniversity — collapse a flat degree-result list into one
 * "flagship" row per university, with the sibling variations kept
 * on the side for the expand-on-click affordance in Quick Check /
 * Advanced Results.
 *
 * Called AFTER the server-side (or client-side) sort. Preserves that
 * sort's intent by using the FIRST occurrence of a university_id as
 * its flagship — so whichever ordering the caller wanted (Compete,
 * Cheapest, Chance, Value, Salary) automatically flows through.
 *
 * A uni with only one matching row skips the expand chip (nothing to
 * expand). Groups without a `university_id` field are left flat so a
 * malformed row never disappears silently.
 */
export function groupByUniversity(results) {
  if (!Array.isArray(results)) return [];
  const seen = new Map();      // uni_id -> { flagship, variations, index }
  const orphans = [];          // rows without a university_id
  results.forEach((r, i) => {
    const uid = r?.university_id;
    if (!uid) {
      orphans.push(r);
      return;
    }
    if (!seen.has(uid)) {
      seen.set(uid, { university_id: uid, flagship: r, variations: [], index: i });
    } else {
      seen.get(uid).variations.push(r);
    }
  });
  // Preserve the caller's original order by re-sorting on the
  // flagship's position in `results`. Orphans go last so they're
  // still visible.
  const groups = Array.from(seen.values()).sort((a, b) => a.index - b.index);
  return groups.map((g) => ({
    university_id: g.university_id,
    flagship: g.flagship,
    variations: g.variations,
    count: 1 + g.variations.length,
  })).concat(orphans.map((r) => ({
    university_id: null,
    flagship: r,
    variations: [],
    count: 1,
  })));
}
